<template>
  <!-- Continue with Google (2026-10-31), at the top of the sign-in form:
       Google's own button (Google Identity Services draws it, in Google's
       words and look, as their branding rules ask), the Terms line (a first
       Google sign-in makes an account), then a thin "or" before the other
       ways in. If Google's script can't load (blocked, offline) the whole
       block steps aside and the form is as it was. -->
  <div v-if="!unavailable" class="auth-google" data-testid="auth-google">
    <div ref="host" class="google-button" data-testid="auth-google-button"></div>
    <p v-if="error" class="google-err" role="alert" data-testid="auth-google-error">{{ error }}</p>
    <p class="google-terms" data-testid="auth-google-terms">
      By continuing, you agree to the
      <a :href="termsUrl" target="_blank" rel="noopener noreferrer">Terms</a>
      and
      <a :href="privacyUrl" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
    </p>
    <div class="auth-or" data-testid="auth-or"><span>or</span></div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { loadGoogleIdentity } from '@/utils/google-identity';

const props = defineProps({
  clientId: { type: String, required: true },
  error: { type: String, default: '' },
  termsUrl: { type: String, required: true },
  privacyUrl: { type: String, required: true },
});
const emit = defineEmits(['credential']);
const host = ref(null);
const unavailable = ref(false);

// GIS draws a button between 200 and 400 px wide; fill the form's width.
function buttonWidth() {
  const w = Math.floor(host.value?.clientWidth || 0);
  return Math.max(200, Math.min(400, w || 320));
}

onMounted(async () => {
  let gis;
  try {
    gis = await loadGoogleIdentity();
  } catch (e) {
    unavailable.value = true;
    return;
  }
  if (!host.value) return;
  gis.initialize({
    client_id: props.clientId,
    callback: (response) => { if (response?.credential) emit('credential', response.credential); },
    ux_mode: 'popup',
    auto_select: false,
    itp_support: true,
  });
  gis.renderButton(host.value, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'center',
    width: buttonWidth(),
  });
});
</script>

<style scoped>
.auth-google { margin: 4px 0 2px; }
.google-button {
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 44px;
}
.google-err { margin: 10px 0 0; font-size: 13px; color: #ffb4a8; }
.google-terms {
  margin: 10px 2px 0; font-size: 12.5px; line-height: 1.55; text-align: center;
  color: rgba(232, 216, 255, 0.62);
}
.google-terms a {
  /* The padding is the tap target; the negative margin keeps the sentence
     spaced as if it were not there. */
  display: inline-block; padding: 8px 4px; margin: -8px -4px; color: #d8b87a;
  text-decoration: underline; text-underline-offset: 2px;
}
.google-terms a:hover { color: #fff7e6; }
.auth-or {
  display: flex; align-items: center; gap: 12px;
  margin: 14px 0 12px;
  font-size: 12px; letter-spacing: 0.12em; text-transform: lowercase;
  color: rgba(232, 216, 255, 0.5);
}
.auth-or::before, .auth-or::after {
  content: ''; flex: 1; height: 1px; background: rgba(240, 217, 163, 0.18);
}
</style>
